// 언어력 — 어휘(유의어·반의어·뜻·사자성어)와 독해(안내문 · 규정을 상황에 적용 · 설명하는 글).
// 오답 보기 공정성: 같은 뜻 무리(유의어가 겹치는 낱말)와 같은 묶음(뜻이 가까운 낱말)은 서로의 오답 보기로 쓰지 않는다.
import type { ChoiceQuestion } from './ChoiceDrill';
import { IDIOMS, PASSAGES, WORDS } from './langData';
import { hasBatchim, int, makeRng, pick, shuffle, type Rng } from './rng';

export type LangKind = 'syn' | 'ant' | 'mean' | 'idiom' | 'idiomName' | 'notice' | 'refund' | 'leave' | 'passage';

export interface LangQuestion extends ChoiceQuestion {
  kind: LangKind;
  ask: string;
  options: string[];
  title?: string;
  body?: string[];
}

export const LANG_COUNT = 8;

export interface Entry { w: string; m: string; syn: string[]; ant: string[]; d: number; g?: string }
const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
export const ENTRIES: Entry[] = WORDS.map(([w, m, syn, ant, d, g]) => ({ w, m, syn: list(syn), ant: list(ant), d, g }));
export const IDIOM_ENTRIES: Entry[] = IDIOMS.map(([w, m, d, g]) => ({ w, m, syn: [], ant: [], d, g }));
const ALL_WORDS = [...new Set(ENTRIES.flatMap((e) => [e.w, ...e.syn, ...e.ant]))];

// 같은 뜻 무리 — 표제어와 유의어를 묶고, 유의어가 겹치면 합친다 (예: 공헌=기여=일조)
const parent = new Map<string, string>();
function find(x: string): string {
  const p = parent.get(x);
  if (p === undefined || p === x) return x;
  const r = find(p);
  parent.set(x, r);
  return r;
}
for (const e of ENTRIES) for (const s of e.syn) {
  const a = find(e.w), b = find(s);
  if (a !== b) parent.set(a, b);
}

/** 이 낱말과 뜻이 가까워 오답 보기로 쓰면 안 되는 말들 — 같은 뜻 무리, 같은 묶음, 반의어와 같은 뜻인 말 */
export function nearWords(e: Entry, pool: Entry[] = ENTRIES): Set<string> {
  const out = new Set<string>();
  const roots = new Set([find(e.w), ...e.ant.map(find)]);
  for (const f of pool) {
    if (f === e || (e.g && f.g === e.g) || roots.has(find(f.w))) for (const x of [f.w, ...f.syn, ...f.ant]) out.add(x);
  }
  for (const x of ALL_WORDS) if (roots.has(find(x))) out.add(x);
  return out;
}

const gwa = (w: string) => `'${w}'${hasBatchim(w) ? '과' : '와'}`;
const eul = (w: string) => `${w}${hasBatchim(w) ? '을' : '를'}`;
const won = (x: number) => `${x.toLocaleString('ko-KR')}원`;

type Made = Omit<LangQuestion, 'answer' | 'n' | 'limitSec' | 'options'> & { right: string; wrong: string[] };

function vocabSyn(r: Rng, e: Entry): Made {
  const near = nearWords(e);
  const traps = e.ant.length && r() < 0.5 ? [pick(r, e.ant)] : []; // 반의어를 함정 보기로
  const right = pick(r, e.syn);
  return {
    kind: 'syn', ask: `${gwa(e.w)} 뜻이 가장 비슷한 낱말은?`, right,
    wrong: [...traps, ...shuffle(r, ALL_WORDS.filter((x) => !near.has(x))).slice(0, 3 - traps.length)],
    explain: `${e.w}: ${e.m} → 비슷한 말 ${e.syn.join(', ')}${e.ant.length ? ` · 반대말 ${e.ant.join(', ')}` : ''}`,
  };
}

function vocabAnt(r: Rng, e: Entry): Made {
  const near = nearWords(e);
  const traps = e.syn.length && r() < 0.5 ? [pick(r, e.syn)] : []; // 유의어를 함정 보기로
  return {
    kind: 'ant', ask: `${gwa(e.w)} 뜻이 반대인 낱말은?`, right: pick(r, e.ant),
    wrong: [...traps, ...shuffle(r, ALL_WORDS.filter((x) => !near.has(x))).slice(0, 3 - traps.length)],
    explain: `${e.w}: ${e.m} → 반대말 ${e.ant.join(', ')}${e.syn.length ? ` · 비슷한 말 ${e.syn.join(', ')}` : ''}`,
  };
}

function vocabMean(r: Rng, e: Entry, pool: Entry[], kind: 'mean' | 'idiom'): Made {
  const near = nearWords(e, pool);
  return {
    kind, ask: `'${e.w}'의 뜻으로 가장 알맞은 것은?`, right: e.m,
    wrong: shuffle(r, pool.filter((f) => !near.has(f.w))).slice(0, 3).map((f) => f.m),
    explain: `${e.w}: ${e.m}`,
  };
}

function idiomName(r: Rng, e: Entry): Made {
  const near = nearWords(e, IDIOM_ENTRIES);
  return {
    kind: 'idiomName', ask: `다음 뜻을 가진 사자성어는? — "${e.m}"`, right: e.w,
    wrong: shuffle(r, IDIOM_ENTRIES.filter((f) => !near.has(f.w))).slice(0, 3).map((f) => f.w),
    explain: `${e.w}: ${e.m}`,
  };
}

// ---- 읽기: 실용문 (숫자·조건을 바꿔 가며 새로 만든다) ----
interface Fact { t: string; ok: boolean }
/** 문장 묶음 — 같은 항목에 대한 참·거짓 문장. why = 근거가 되는 글의 줄 */
interface FactGroup { why: number; items: Fact[] }
interface Doc { kind: 'notice' | 'refund' | 'leave'; title: string; body: string[]; facts: FactGroup[] }

const DAYS = ['월', '화', '수', '목', '금', '토', '일'];
const EVENTS: [string, string, string][] = [
  ['청년 요리 교실', '구민회관 3층 조리실', '앞치마'],
  ['직장인 재테크 강좌', '구립도서관 2층 강의실', '필기도구'],
  ['주말 목공 체험', '마을공동체센터 작업장', '목장갑'],
  ['반려식물 가꾸기 교실', '시민정원 온실', '빈 화분'],
  ['스마트폰 사진 강좌', '청소년수련관 1층 강당', '충전한 스마트폰'],
  ['취업 면접 특강', '일자리센터 4층 회의실', '이력서 1부'],
];

function noticeDoc(r: Rng): Doc {
  const [name, place, prep] = pick(r, EVENTS);
  const month = int(r, 3, 11), day = int(r, 1, 28), wd = int(r, 0, 6), start = int(r, 9, 15), hours = int(r, 2, 4);
  const lo = pick(r, [19, 20]), hi = pick(r, [29, 34, 39]), cap = pick(r, [20, 30, 40, 50]);
  const lottery = r() < 0.5, fee = r() < 0.5 ? 0 : pick(r, [5000, 10000, 15000]), online = r() < 0.6, cancel = int(r, 1, 3);
  const other = (wd + int(r, 1, 6)) % 7;
  return {
    kind: 'notice', title: `○○구 ${name} 참가자 모집`,
    body: [
      `· 일시: ${month}월 ${day}일(${DAYS[wd]}) ${start}시~${start + hours}시`,
      `· 장소: ${place}`,
      `· 대상: 만 ${lo}~${hi}세 구민 ${cap}명 (${lottery ? '신청자 중 추첨' : '선착순'})`,
      `· 참가비: ${fee ? `${won(fee)} (재료비 포함)` : '무료'}`,
      `· 신청: ${online ? '구청 누리집 또는 구청 방문' : '구청 방문 접수만 가능'}`,
      `· 준비물: ${prep}`,
      `· 취소: 행사 ${cancel}일 전까지 할 수 있으며, 연락 없이 빠지면 다음 행사 신청이 제한됩니다.`,
    ],
    facts: [
      { why: 0, items: [{ t: `행사는 ${DAYS[wd]}요일에 열린다.`, ok: true }, { t: `행사는 ${DAYS[other]}요일에 열린다.`, ok: false }] },
      { why: 0, items: [{ t: `행사는 ${hours}시간 동안 진행된다.`, ok: true }, { t: `행사는 ${hours + 1}시간 동안 진행된다.`, ok: false }] },
      { why: 2, items: [{ t: `만 ${hi}세도 신청할 수 있다.`, ok: true }, { t: `만 ${hi + 1}세도 신청할 수 있다.`, ok: false }, { t: `만 ${lo - 1}세도 신청할 수 있다.`, ok: false }] },
      { why: 2, items: lottery
        ? [{ t: '먼저 신청한다고 뽑히는 것은 아니다.', ok: true }, { t: '먼저 신청한 순서대로 뽑는다.', ok: false }]
        : [{ t: '먼저 신청한 순서대로 뽑는다.', ok: true }, { t: '신청자 가운데 추첨으로 뽑는다.', ok: false }] },
      { why: 2, items: [{ t: `최대 ${cap}명이 참가할 수 있다.`, ok: true }, { t: `최대 ${cap + 10}명이 참가할 수 있다.`, ok: false }] },
      { why: 3, items: fee
        ? [{ t: '참가비를 내야 한다.', ok: true }, { t: '참가비 없이 참여할 수 있다.', ok: false }]
        : [{ t: '참가비 없이 참여할 수 있다.', ok: true }, { t: '재료비를 따로 내야 한다.', ok: false }] },
      { why: 4, items: online
        ? [{ t: '인터넷으로도 신청할 수 있다.', ok: true }, { t: '구청에 가야만 신청할 수 있다.', ok: false }]
        : [{ t: '구청에 가야만 신청할 수 있다.', ok: true }, { t: '인터넷으로도 신청할 수 있다.', ok: false }] },
      { why: 5, items: [{ t: `${eul(prep)} 챙겨 가야 한다.`, ok: true }] },
      { why: 6, items: cancel >= 2
        ? [{ t: '행사 하루 전에는 취소할 수 없다.', ok: true }, { t: '행사 하루 전에도 취소할 수 있다.', ok: false }]
        : [{ t: '행사 하루 전에도 취소할 수 있다.', ok: true }, { t: '행사 당일에도 취소할 수 있다.', ok: false }] },
      { why: 6, items: [{ t: '연락 없이 빠지면 다음 행사 신청이 제한된다.', ok: true }, { t: '연락 없이 빠져도 다음 행사에는 신청할 수 있다.', ok: false }] },
    ],
  };
}

function refundDoc(r: Rng): Doc {
  const a = pick(r, [3, 5, 7]), b = a + pick(r, [7, 14]), p = pick(r, [50, 70, 80]);
  const x1 = int(r, 1, a), x2 = int(r, a + 1, b), x3 = b + int(r, 1, 10);
  return {
    kind: 'refund', title: `${pick(r, ['온라인 문구점', '○○ 전자', '△△ 의류', '□□ 서점'])} 환불·교환 안내`,
    body: [
      `1. 구매 후 ${a}일 이내, 포장을 뜯지 않은 상품은 전액 환불합니다.`,
      `2. 구매 후 ${a}일 이내라도 포장을 뜯었다면 구매 금액의 ${p}%만 환불합니다.`,
      `3. 구매 후 ${a + 1}일부터 ${b}일까지는 같은 상품으로 교환만 할 수 있습니다.`,
      `4. 구매 후 ${b}일이 지나면 환불과 교환이 되지 않습니다.`,
      '5. 상품 불량은 기간과 관계없이 전액 환불하며, 이때 왕복 배송비는 판매자가 냅니다. 단순 변심일 때의 배송비는 구매자가 냅니다.',
    ],
    facts: [
      { why: 0, items: [{ t: `구매 ${x1}일째 포장을 뜯지 않은 상품을 반품하면 전액 돌려받는다.`, ok: true }, { t: `구매 ${x2}일째 포장을 뜯지 않은 상품을 반품하면 전액 돌려받는다.`, ok: false }] },
      { why: 1, items: [{ t: `구매 ${x1}일째 포장을 뜯은 상품을 반품하면 ${p}%를 돌려받는다.`, ok: true }, { t: `구매 ${x1}일째에는 포장을 뜯은 상품도 전액 돌려받는다.`, ok: false }] },
      { why: 2, items: [{ t: `구매 ${x2}일째에는 같은 상품으로 바꿀 수 있다.`, ok: true }, { t: `구매 ${x2}일째에는 다른 상품으로 바꿀 수 있다.`, ok: false }, { t: `구매 ${x2}일째에 반품하면 일부를 돌려받는다.`, ok: false }] },
      { why: 3, items: [{ t: `구매 ${x3}일째에는 단순 변심이면 교환도 할 수 없다.`, ok: true }, { t: `구매 ${x3}일째에도 같은 상품으로는 교환할 수 있다.`, ok: false }] },
      { why: 4, items: [{ t: `구매 ${x3}일째에 불량을 발견해도 전액 돌려받는다.`, ok: true }, { t: `구매 ${x3}일째에는 불량이어도 환불받을 수 없다.`, ok: false }] },
      { why: 4, items: [{ t: '단순 변심으로 반품할 때 배송비는 구매자가 낸다.', ok: true }, { t: '단순 변심으로 반품할 때 배송비는 판매자가 낸다.', ok: false }] },
      { why: 4, items: [{ t: '불량 상품을 반품할 때 배송비는 판매자가 낸다.', ok: true }, { t: '불량 상품이라도 배송비는 구매자가 낸다.', ok: false }] },
    ],
  };
}

function leaveDoc(r: Rng): Doc {
  const k = pick(r, [2, 3, 5]), m = pick(r, [3, 4, 5]), c = pick(r, [3, 5, 10]), months = int(r, 2, 11), half = pick(r, [2, 4, 6]);
  return {
    kind: 'leave', title: `${pick(r, ['○○ 주식회사', '△△ 물류', '□□ 디자인'])} 휴가 규정`,
    body: [
      '1. 입사 후 1년이 지나면 연차 휴가 15일이 생긴다. 입사 1년이 안 된 직원은 한 달 개근할 때마다 연차 1일이 생긴다.',
      '2. 반차는 반나절 휴가이며, 반차 2번은 연차 1일로 친다.',
      `3. 휴가는 쓰기 ${k}일 전까지 신청한다. 단, 병가는 당일에 신청할 수 있다.`,
      `4. 연속 ${m}일 이상 쉬려면 팀장과 부서장의 승인을 모두 받아야 하고, 그보다 짧으면 팀장 승인만 받으면 된다.`,
      `5. 그해에 쓰지 못한 연차는 ${c}일까지만 다음 해로 넘길 수 있다.`,
    ],
    facts: [
      { why: 0, items: [{ t: `입사한 지 ${months}개월 된 직원도 연차가 생길 수 있다.`, ok: true }, { t: `입사한 지 ${months}개월 된 직원에게는 연차가 생기지 않는다.`, ok: false }] },
      { why: 1, items: [{ t: `반차를 ${half}번 쓰면 연차 ${half / 2}일을 쓴 셈이다.`, ok: true }, { t: `반차를 ${half}번 쓰면 연차 ${half}일을 쓴 셈이다.`, ok: false }] },
      { why: 2, items: [{ t: '일반 휴가를 하루 전에 신청하면 늦다.', ok: true }, { t: '일반 휴가는 하루 전에만 신청하면 된다.', ok: false }] },
      { why: 2, items: [{ t: '병가는 당일에 신청해도 된다.', ok: true }, { t: '병가도 미리 신청해야만 쓸 수 있다.', ok: false }] },
      { why: 3, items: [{ t: `${m - 1}일 연속 휴가는 팀장 승인만 받으면 된다.`, ok: true }, { t: `${m}일 연속 휴가는 팀장 승인만 받으면 된다.`, ok: false }] },
      { why: 4, items: [{ t: `남은 연차 ${c + int(r, 1, 5)}일을 모두 다음 해로 넘길 수는 없다.`, ok: true }, { t: `남은 연차 ${c + 2}일을 모두 다음 해로 넘길 수 있다.`, ok: false }] },
    ],
  };
}

/** 일치하는 것(참 1 + 거짓 3) 또는 일치하지 않는 것(거짓 1 + 참 3) — 한 항목에서는 한 문장만 고른다 */
function docQuestion(r: Rng, doc: Doc, mismatch: boolean): Made | null {
  const want = !mismatch;
  const groups = shuffle(r, doc.facts);
  const head = groups.find((g) => g.items.some((f) => f.ok === want));
  if (!head) return null;
  const rest = groups.filter((g) => g !== head && g.items.some((f) => f.ok !== want)).slice(0, 3);
  if (rest.length < 3) return null;
  const right = pick(r, head.items.filter((f) => f.ok === want)).t;
  return {
    kind: doc.kind, title: doc.title, body: doc.body,
    ask: mismatch ? '글의 내용과 일치하지 않는 것은?' : '글의 내용과 일치하는 것은?',
    right, wrong: rest.map((g) => pick(r, g.items.filter((f) => f.ok !== want)).t),
    explain: `근거: ${doc.body[head.why]}`,
  };
}

const DOCS = { notice: noticeDoc, refund: refundDoc, leave: leaveDoc };

export function makeLangRound(level: number, seed: number): LangQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const maxD = lv <= 2 ? 1 : lv <= 5 ? 2 : 3;
  const vocabSec = Math.round(26 - lv * 1.3);
  const readSec = 110 - lv * 4;
  const out: LangQuestion[] = [];
  const push = (m: Made | null, limitSec: number) => {
    if (!m) return false;
    const options = shuffle(rng, [m.right, ...m.wrong]);
    const { right, wrong, ...rest } = m;
    void wrong;
    out.push({ ...rest, options, answer: options.indexOf(right), n: options.length, limitSec });
    return true;
  };

  // 어휘 5문제 — 높은 레벨일수록 어려운 낱말 비중을 늘린다
  const words = ENTRIES.filter((e) => e.d <= maxD && (lv < 7 || e.d >= 2 || rng() < 0.3));
  const idioms = IDIOM_ENTRIES.filter((e) => e.d <= maxD);
  const used = new Set<string>();
  const kinds = shuffle(rng, ['syn', 'ant', 'mean', 'idiom', pick(rng, ['syn', 'ant', 'idiomName'])]);
  for (const kind of kinds) {
    for (let guard = 0; guard < 50; guard++) {
      if (kind === 'idiom' || kind === 'idiomName') {
        const e = pick(rng, idioms);
        if (used.has(e.w)) continue;
        used.add(e.w);
        push(kind === 'idiom' ? vocabMean(rng, e, IDIOM_ENTRIES, 'idiom') : idiomName(rng, e), vocabSec);
        break;
      }
      const e = pick(rng, words);
      if (used.has(e.w) || (kind === 'syn' && !e.syn.length) || (kind === 'ant' && !e.ant.length)) continue;
      used.add(e.w);
      push(kind === 'syn' ? vocabSyn(rng, e) : kind === 'ant' ? vocabAnt(rng, e) : vocabMean(rng, e, ENTRIES, 'mean'), vocabSec);
      break;
    }
  }

  // 독해 3문제 — 실용문(규정은 레벨 3부터, '일치하지 않는 것'은 레벨 6부터)과 설명하는 글
  const docKinds: (keyof typeof DOCS)[] = lv <= 2 ? ['notice'] : ['notice', 'refund', 'leave'];
  const passages = shuffle(rng, PASSAGES.filter((p) => p.level <= lv + 1));
  const plan = shuffle(rng, ['doc', 'doc', 'passage']);
  for (const what of plan) {
    if (what === 'passage' && passages.length) {
      const p = passages.shift()!;
      const q = pick(rng, p.qs);
      push({ kind: 'passage', title: p.title, body: [p.text], ask: q.ask, right: q.right, wrong: q.wrong, explain: `정답: ${q.right}` }, readSec);
      continue;
    }
    for (let guard = 0; guard < 20; guard++) {
      if (push(docQuestion(rng, DOCS[pick(rng, docKinds)](rng), lv >= 6 && rng() < 0.5), readSec)) break;
    }
  }
  return out;
}
